<template>
  <v-dialog v-model="dialog" max-width="520px" persistent>
    <v-card class="adjust-card">
      <div class="dialog-header">
        <div>
          <div class="dialog-title">Adjust Stock</div>
          <div class="dialog-subtitle">
            {{ product ? product.name : "" }}
          </div>
        </div>

        <v-btn icon small @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="pa-4">
        <div v-if="product" class="stock-summary">
          <div>
            <div class="summary-label">Current Stock</div>
            <div class="summary-value">{{ currentQty }}</div>
          </div>

          <v-icon color="grey">mdi-arrow-right</v-icon>

          <div>
            <div class="summary-label">New Stock</div>
            <div class="summary-value">{{ adjustedQty || 0 }}</div>
          </div>
        </div>

        <v-alert
          v-if="difference !== 0"
          dense
          outlined
          :type="difference > 0 ? 'success' : 'warning'"
          class="mt-4"
        >
          {{ difference > 0 ? "Increase" : "Decrease" }}
          by {{ Math.abs(difference) }} unit(s)
        </v-alert>

        <v-form ref="form" v-model="isValid" class="mt-4">
          <div class="field-label">New Stock Quantity</div>
          <v-text-field
            v-model.number="adjustedQty"
            outlined
            dense
            type="number"
            min="0"
            hide-details="auto"
            @focus="$event.target.select()"
            :rules="[(v) => v >= 0 || 'Must be 0 or more']"
          />

          <div class="field-label mt-4">Reason</div>
          <v-select
            v-model="reason"
            :items="reasons"
            outlined
            dense
            hide-details="auto"
            :rules="[(v) => !!v || 'Reason is required']"
          />

          <div class="field-label mt-4">Notes</div>
          <v-textarea
            v-model="notes"
            outlined
            dense
            rows="2"
            auto-grow
            hide-details
            placeholder="Optional"
          />
        </v-form>
      </v-card-text>

      <v-divider />

      <v-card-actions class="dialog-actions">
        <v-btn text @click="closeDialog">Cancel</v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          rounded
          dark
          :disabled="!isValid || difference === 0"
          @click="submit"
        >
          Update Stock
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import productService from "../service";

export default {
  name: "InventoryAdjustDialog",

  props: {
    value: Boolean,
    product: Object,
  },

  data() {
    return {
      dialog: false,
      isValid: false,
      adjustedQty: 0,
      reason: null,
      notes: "",
      reasons: [
        "Manual Correction",
        "Inventory Count",
        "Damaged/Lost",
        "Found Extra Stock",
        "Shrinkage/Leakage",
        "Theft",
      ],
    };
  },

  computed: {
    ...mapGetters("global", ["currentUser", "checkRightStatus"]),

    currentQty() {
      return this.product ? Number(this.product.balance || 0) : 0;
    },

    difference() {
      return Number(this.adjustedQty || 0) - this.currentQty;
    },

    adjustmentType() {
      return this.difference > 0 ? "Increase" : "Decrease";
    },
  },

  watch: {
    value(val) {
      this.dialog = val;

      if (val && this.product) {
        this.adjustedQty = Number(this.product.balance || 0);
        this.reason = null;
        this.notes = "";

        this.$nextTick(() => {
          if (this.$refs.form) {
            this.$refs.form.resetValidation();
          }
        });
      }
    },

    dialog(val) {
      this.$emit("input", val);
    },
  },

  methods: {
    closeDialog() {
      this.dialog = false;
      this.$emit("done", false);
    },

    async submit() {
      if (!this.$refs.form.validate()) return;

      if (this.difference === 0) {
        this.$swal({
          toast: true,
          position: "top-end",
          icon: "info",
          title: "No stock change made",
          showConfirmButton: false,
          timer: 3000,
        });
        return;
      }

      const result = await this.$swal({
        title: "Confirm Stock Adjustment",
        html: `
          <div style="text-align:left">
            <b>Product:</b> ${this.product.name}<br/>
            <b>Current Stock:</b> ${this.currentQty}<br/>
            <b>New Stock:</b> ${this.adjustedQty}<br/>
            <b>Change:</b> ${this.adjustmentType} ${Math.abs(this.difference)} unit(s)<br/>
            <b>Reason:</b> ${this.reason}
          </div>
        `,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Update Stock",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
      });

      if (!result.value) return;

      const payload = {
        ProductId: this.product.id,
        BusinessId: this.product.BusinessId,
        UserId: this.currentUser.id,
        reason: this.reason,
        adjustmentType: this.adjustmentType,
        adjustmentQuantity: this.difference,
        notes: this.notes || "",
      };

      try {
        await productService.stockAdjustment.create(payload);

        this.$swal({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Stock updated successfully",
          showConfirmButton: false,
          timer: 3000,
        });

        this.dialog = false;
        this.$emit("done", true);
      } catch (err) {
        console.error("Stock adjustment failed:", err);

        this.$swal({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "Unable to update stock",
          showConfirmButton: false,
          timer: 3000,
        });
      }
    },
  },
};
</script>

<style scoped>
.adjust-card {
  border-radius: 20px;
  overflow: hidden;
}

.dialog-header {
  padding: 18px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.dialog-title {
  font-size: 20px;
  font-weight: 900;
  color: #0f172a;
}

.dialog-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 3px;
}

.stock-summary {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 16px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 12px;
  text-align: center;
}

.summary-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 800;
}

.summary-value {
  font-size: 28px;
  font-weight: 900;
  color: #0f172a;
  margin-top: 4px;
}

.field-label {
  font-size: 12px;
  font-weight: 800;
  color: #475569;
  margin-bottom: 6px;
}

.dialog-actions {
  padding: 12px 16px;
}
</style>